import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { MarketCard, MarketDetailsDialog } from '../components/MarketCard';
import { 
  ArrowRight, 
  Wifi, 
  Cable, 
  Phone, 
  Building2,
  Users, 
  Award,
  CheckCircle,
  Truck,
  Wrench,
  Zap
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Default content (fallback)
const DEFAULT_CONTENT = {
  hero: {
    badge_text: "Now Recruiting Field Technicians",
    title_line1: "SKILLED TECHS",
    title_line2: "WANTED",
    description: "Join Mighty Link's network of independent contractors. We connect experienced installation technicians with top telecommunications providers for cable TV, internet, telephone, and fiber optic projects.",
    image_url: "https://images.pexels.com/photos/14541775/pexels-photo-14541775.jpeg",
    cta_primary: "Join Our Team",
    cta_secondary: "Our Services"
  },
  services: [
    { title: "Fiber Optic Installation", description: "Professional fiber optic cable installation for residential and commercial properties.", icon: "cable" },
    { title: "Internet & Cable TV", description: "Complete installation services for high-speed internet and cable television systems.", icon: "wifi" },
    { title: "Telephone Systems", description: "VoIP and traditional telephone line installation and setup for homes and businesses.", icon: "phone" },
    { title: "Commercial Installations", description: "Large-scale telecommunications infrastructure for businesses and commercial buildings.", icon: "building" }
  ],
  about: {
    title_line1: "WHY WORK WITH",
    title_line2: "MIGHTY LINK",
    description: "We're a subcontracted field services company partnering with major telecommunications providers. Our technicians handle installations for cable TV, internet, telephone, and fiber optics—both residential and commercial.",
    image_url: "https://images.pexels.com/photos/5691589/pexels-photo-5691589.jpeg",
    benefits: ["Competitive pay per job", "Flexible work schedule", "Training & certifications", "Equipment allowances", "Growth opportunities", "Nationwide projects"]
  },
  requirements: [
    { title: "Experience", description: "Cable, internet, phone, or fiber installation experience preferred. We value hands-on expertise and professional certifications.", icon: "users" },
    { title: "Equipment", description: "Own truck and tools are a plus. We provide equipment allowances and tool reimbursement programs for qualified technicians.", icon: "truck" },
    { title: "Certifications", description: "Industry certifications are valued. We also provide training opportunities to help you grow your skills and advance your career.", icon: "award" }
  ],
  cta: {
    title_line1: "READY TO",
    title_line2: "GET STARTED?",
    description: "Apply now to join our network of professional installation technicians. Quick application process—start working with top providers.",
    button_text: "Apply Now"
  }
};

const iconMap = {
  cable: <Cable className="w-7 h-7 sm:w-8 sm:h-8 text-violet-400" />,
  wifi: <Wifi className="w-7 h-7 sm:w-8 sm:h-8 text-cyan-400" />,
  phone: <Phone className="w-7 h-7 sm:w-8 sm:h-8 text-violet-300" />,
  building: <Building2 className="w-7 h-7 sm:w-8 sm:h-8 text-cyan-300" />,
  users: <Users size={24} className="text-white" />,
  truck: <Truck size={24} className="text-white" />,
  award: <Award size={24} className="text-white" />
};

export default function LandingPage() {
  const [content, setContent] = useState(DEFAULT_CONTENT);
  const [loading, setLoading] = useState(true);
  const [markets, setMarkets] = useState([]);
  const [selectedMarket, setSelectedMarket] = useState(null);
  const [marketsError, setMarketsError] = useState(false);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await axios.get(`${API_URL}/site-content`);
        if (response.data && Object.keys(response.data).length > 0) {
          setContent({ ...DEFAULT_CONTENT, ...response.data });
        }
      } catch {
        /* falls back to DEFAULT_CONTENT */
      } finally {
        setLoading(false);
      }
    };
    const fetchMarkets = async () => {
      try {
        const res = await axios.get(`${API_URL}/markets`);
        setMarkets(res.data || []);
      } catch { setMarketsError(true); }
    };
    fetchContent();
    fetchMarkets();
  }, []);

  const { hero, services, about, requirements, cta } = content;

  return (
    <div className="min-h-screen bg-[#0A0A0F]">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Background effects */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-violet-600 rounded-full filter blur-[150px] opacity-20"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-cyan-600 rounded-full filter blur-[150px] opacity-15"></div>
          <div className="absolute inset-0 bg-[linear-gradient(rgba(139,92,246,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(139,92,246,0.03)_1px,transparent_1px)] bg-[size:60px_60px]"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-28 relative z-10">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            {/* Left Content */}
            <div className="animate-fade-in-up text-center lg:text-left">
              <div className="inline-flex items-center gap-2 bg-violet-500/10 border border-violet-500/20 text-violet-300 px-4 py-2 rounded-full text-xs sm:text-sm font-medium mb-6">
                <Zap size={14} className="text-cyan-400" />
                <span>{hero.badge_text}</span>
              </div>
              
              <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-extrabold leading-tight mb-6" style={{ fontFamily: 'Syne, sans-serif' }}>
                <span className="silver-text">{hero.title_line1}</span>
                <span className="block gradient-text">{hero.title_line2}</span>
              </h1>
              
              <p className="text-base sm:text-lg text-slate-400 mb-8 max-w-xl mx-auto lg:mx-0">
                {hero.description}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start">
                <Link to="/register">
                  <Button 
                    size="lg" 
                    className="w-full sm:w-auto btn-gradient text-white border-0 text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6"
                    data-testid="hero-apply-btn"
                  >
                    {hero.cta_primary}
                    <ArrowRight className="ml-2" size={18} />
                  </Button>
                </Link>
                <a href="#services">
                  <Button 
                    variant="outline" 
                    size="lg"
                    className="w-full sm:w-auto text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 border-slate-700 bg-slate-800/50 text-slate-300 hover:bg-slate-700 hover:text-white"
                    data-testid="hero-learn-btn"
                  >
                    {hero.cta_secondary}
                  </Button>
                </a>
              </div>
            </div>
            
            {/* Right Image */}
            <div className="relative animate-fade-in-up stagger-2 mt-8 lg:mt-0">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-violet-600/20 to-cyan-600/20 rounded-2xl blur-2xl"></div>
                <div className="relative rounded-2xl overflow-hidden border border-slate-700/50">
                  <img 
                    src={hero.image_url}
                    alt="Cable technician working"
                    className="w-full h-[300px] sm:h-[380px] lg:h-[450px] object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0F] via-transparent to-transparent opacity-60"></div>
                </div>
                
                <div className="absolute -bottom-4 -left-4 sm:-bottom-6 sm:-left-6 bg-[#12121A] p-4 sm:p-5 rounded-xl border border-violet-500/30 shadow-xl glow-violet">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-violet-500 to-cyan-500 rounded-lg flex items-center justify-center">
                      <Wrench className="text-white" size={20} />
                    </div>
                    <div>
                      <p className="font-semibold text-white text-sm sm:text-base">Now Hiring</p>
                      <p className="text-slate-500 text-xs sm:text-sm">All skill levels</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-16 sm:py-20 lg:py-24 bg-[#050507] relative">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(139,92,246,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(139,92,246,0.02)_1px,transparent_1px)] bg-[size:40px_40px]"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 sm:mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
              <span className="silver-text">INSTALLATION</span>{' '}
              <span className="gradient-text">SERVICES</span>
            </h2>
            <p className="text-base sm:text-lg text-slate-500 max-w-2xl mx-auto px-4">
              We provide skilled technicians for residential and commercial telecommunications installations
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {services.map((service, index) => (
              <div 
                key={`${service.title}-${index}`}
                className="bg-[#12121A] p-5 sm:p-6 rounded-xl border border-slate-800 hover:border-violet-500/30 transition-all duration-300 card-hover animate-fade-in-up"
                style={{ animationDelay: `${index * 0.1}s` }}
                data-testid={`service-card-${index}`}
              >
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-violet-500/20 to-cyan-500/20 rounded-xl flex items-center justify-center mb-4 border border-violet-500/20">
                  {iconMap[service.icon] || iconMap.cable}
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-white mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>
                  {service.title}
                </h3>
                <p className="text-slate-500 text-sm sm:text-base">
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-16 sm:py-20 lg:py-24 bg-[#0A0A0F] relative overflow-hidden">
        <div className="absolute top-1/2 left-0 w-96 h-96 bg-violet-600 rounded-full filter blur-[200px] opacity-10 -translate-y-1/2"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            {/* Image */}
            <div className="relative order-2 lg:order-1">
              <div className="absolute -inset-4 bg-gradient-to-r from-violet-600/10 to-cyan-600/10 rounded-2xl blur-xl"></div>
              <img 
                src={about.image_url}
                alt="Technician explaining service to customer"
                className="relative rounded-2xl w-full h-[300px] sm:h-[350px] lg:h-[400px] object-cover border border-slate-800"
              />
            </div>
            
            {/* Content */}
            <div className="order-1 lg:order-2">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 sm:mb-6" style={{ fontFamily: 'Syne, sans-serif' }}>
                <span className="silver-text">{about.title_line1}</span>{' '}
                <span className="gradient-text">{about.title_line2}</span>?
              </h2>
              <p className="text-base sm:text-lg text-slate-400 mb-6 sm:mb-8">
                {about.description}
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-6 sm:mb-8">
                {about.benefits.map((benefit, index) => (
                  <div key={`${benefit}-${index}`} className="flex items-center gap-2 sm:gap-3">
                    <CheckCircle className="text-violet-500 flex-shrink-0" size={18} />
                    <span className="text-slate-300 text-sm sm:text-base">{benefit}</span>
                  </div>
                ))}
              </div>
              
              <Link to="/register">
                <Button 
                  size="lg" 
                  className="w-full sm:w-auto btn-gradient text-white border-0"
                  data-testid="about-apply-btn"
                >
                  Start Your Application
                  <ArrowRight className="ml-2" size={18} />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* What We Need Section */}
      <section className="py-16 sm:py-20 lg:py-24 bg-[#050507] relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-violet-500/50 to-transparent"></div>
        <div className="absolute bottom-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 sm:mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
              <span className="silver-text">WHAT WE'RE</span>{' '}
              <span className="gradient-text">LOOKING FOR</span>
            </h2>
            <p className="text-base sm:text-lg text-slate-500 max-w-2xl mx-auto px-4">
              We recruit experienced installation technicians ready to work with top providers
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
            {requirements.map((req, index) => {
              const colors = ['from-violet-500 to-violet-600', 'from-cyan-500 to-cyan-600', 'from-violet-400 to-cyan-400'];
              const hoverColors = ['hover:border-violet-500/30', 'hover:border-cyan-500/30', 'hover:border-violet-500/30'];
              
              return (
                <div key={`${req.title}-${index}`} className={`bg-[#12121A] p-6 sm:p-8 rounded-xl border border-slate-800 ${hoverColors[index % 3]} transition-colors ${index === 2 ? 'sm:col-span-2 lg:col-span-1' : ''}`}>
                  <div className={`w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br ${colors[index % 3]} rounded-xl flex items-center justify-center mb-4 sm:mb-6`}>
                    {iconMap[req.icon] || iconMap.users}
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-white mb-3 sm:mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>{req.title}</h3>
                  <p className="text-slate-500 text-sm sm:text-base">
                    {req.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Available Markets Section */}
      {(markets.length > 0 || marketsError) && (
        <section id="markets" className="py-16 sm:py-20 lg:py-24 bg-[#0A0A0F] relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-violet-500/50 to-transparent"></div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="text-center mb-12 sm:mb-16">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 sm:mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
                <span className="silver-text">AVAILABLE</span>{' '}
                <span className="gradient-text">MARKETS</span>
              </h2>
              <p className="text-base sm:text-lg text-slate-500 max-w-2xl mx-auto px-4">
                Explore our current service areas and available opportunities
              </p>
            </div>

            {marketsError && (
              <p className="text-center text-slate-400" data-testid="markets-error">
                We couldn't load the available markets right now. Please refresh the page in a moment.
              </p>
            )}
            <div className="flex flex-wrap justify-center gap-4 sm:gap-6" data-testid="markets-grid">
              {markets.map((market) => (
                <MarketCard key={market.id} market={market} onMore={setSelectedMarket} />
              ))}
            </div>
            <MarketDetailsDialog market={selectedMarket} onClose={() => setSelectedMarket(null)} />
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-16 sm:py-20 lg:py-24 bg-gradient-to-br from-violet-900/50 via-[#0A0A0F] to-cyan-900/30 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-64 h-64 bg-violet-600 rounded-full filter blur-[120px] opacity-20"></div>
          <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-cyan-600 rounded-full filter blur-[120px] opacity-20"></div>
        </div>
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold mb-4 sm:mb-6" style={{ fontFamily: 'Syne, sans-serif' }}>
            <span className="silver-text">{cta.title_line1}</span>{' '}
            <span className="gradient-text">{cta.title_line2}</span>
          </h2>
          <p className="text-base sm:text-lg text-slate-400 mb-8 sm:mb-10 max-w-2xl mx-auto px-4">
            {cta.description}
          </p>
          <Link to="/register">
            <Button 
              size="lg" 
              className="w-full sm:w-auto btn-silver text-slate-900 font-semibold text-base sm:text-lg px-8 sm:px-12 py-5 sm:py-6"
              data-testid="cta-apply-btn"
            >
              {cta.button_text}
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
